import { createContext, useContext, useMemo } from 'react'

import { useObjects } from 'data/hooks/objects'
import { useViews } from 'data/hooks/views'
import { useProcessFilter } from 'features/records/components/RecordFilters'

import { getActionNavigateToTarget } from './utils/getActionNavigateToTarget'
import { useActionsFromObjectId } from './helpers'
import type { ActionsContext as ActionsContextType } from './types'

export const ActionsContext = createContext<ActionsContextType | undefined>(undefined)

export function useActionsContext() {
    const context = useContext(ActionsContext)
    if (!context) {
        throw new Error(
            "Can't access actions context provider, is your component wrapped in <ActionsContextProvider/> ?"
        )
    }

    return context
}

export function useActionButtons(
    selectedItems: ActionButton[],
    record: RecordDto,
    object: ObjectDto,
    additionalActions: ActionDto[] = []
) {
    const { data: actions } = useActionsFromObjectId(object?._sid)
    const processFilter = useProcessFilter()

    const { data: views = [] } = useViews()
    const { data: objects = [] } = useObjects()

    // want to re-evaluate available buttons if the record changes
    // or the user's role changes.
    return useMemo(() => {
        if (!selectedItems || !actions || !record) return []

        const actionMap = new Map(
            [...additionalActions, ...actions].map((action) => [action._sid, action])
        )

        const result: { action: ActionDto; config: ActionButton }[] = []
        for (const actionButton of selectedItems) {
            // Skip if conditions exist and record doesn't match
            if (
                actionButton.conditions &&
                processFilter([record], actionButton.conditions).length === 0
            ) {
                continue
            }

            const action = actionMap.get(actionButton.id)
            if (!action) continue

            // This is a navigation target, so we need to check if it should be hidden.
            const navigateToTarget = getActionNavigateToTarget(action, record, views, objects)
            if (navigateToTarget.isNavigationTarget && navigateToTarget.shouldHide) continue

            result.push({ action, config: actionButton })
        }

        return result
    }, [selectedItems, actions, record, additionalActions, processFilter, views, objects])
}
